<template>
  <v-container v-if="!isReleaseLoading" class="h-100 pa-4" fluid>
    <v-row justify="center">
      <v-sheet class="bg-background flex-1-1 px-4" max-width="400px">
        <v-img :src="LOGO_HEADER" contain />
        <div class="text-h5 text-center mb-8 font-weight-medium">Talent Release</div>
      </v-sheet>
    </v-row>
    <v-row v-if="errors" justify="center">
      <v-sheet class="bg-background flex-1-1 px-4">
        <div class="text-h5 text-center mb-8 font-weight-medium">Error</div>
        <v-row class="mb-4">
          <v-col cols="12" class="text-center">
            <span class="text-body-2">There was an error retrieving your release.</span>
            <span class="text-body-2">Please check the link or contact support.</span>
          </v-col>
        </v-row>
      </v-sheet>
    </v-row>
    <v-row justify="center">
      <v-sheet class="bg-background flex-1-1 px-4">
        <ReleaseSignComponent
          v-if="!errors && showReleaseForm"
          :form="talenForm"
          :formFields="fields"
          @sig-completed="handleSigUpload">
        </ReleaseSignComponent>
      </v-sheet>
    </v-row>
    <v-row v-if="!errors && !showReleaseForm" justify="center">
      <v-sheet class="bg-background flex-1-1 px-4">
        <div class="text-h5 text-center mb-8 font-weight-medium">Release Signed</div>
        <v-row class="mb-4">
          <v-col cols="12" class="text-center">
            <span class="text-body-2">Thank you for signing the release.</span>
            <span class="text-body-2">You can download the signed release below.</span>
            <span class="text-body-2">If you need to make changes, click resign.</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="text-center">
            <FullWidthButton color="secondary" @click="showReleaseForm = true">
              Resign
            </FullWidthButton>
          </v-col>
          <v-col cols="12" md="6" class="text-center">
            <a
              :href="release.release?.url"
              target="_blank"
              download
              class="text-decoration-none text-primary text-body-2 font-weight-medium">
              <FullWidthButton> Download </FullWidthButton>
            </a>
          </v-col>
        </v-row>
      </v-sheet>
    </v-row>
  </v-container>
  <v-overlay :model-value="overlay" class="align-center justify-center">
    <v-progress-circular color="primary" size="64" indeterminate></v-progress-circular>
  </v-overlay>
</template>
<script setup lang="ts">
import ReleaseSignComponent from '@/components/release/ReleaseSignComponent.vue';
import { createProgressHandler, uploadSubmissionRelease } from '@/controllers';
import {
  IAssetDTO,
  IReleaseDTO,
  ISubmissionCastCrewDTO,
  ReleaseDTO,
  ReleaseTypeDTO,
  ReleaseTypeEnum,
  SubmissionCastCrewDTO,
  SubmissionDTO,
} from '@/dtos';
import { browser, currentDate, IBaseFormFields, LOGO_HEADER, userIP } from '@/helpers';
import { DEFAULT_TALENT_FORM, useFormTemplateStore, useReleaseStore } from '@/stores';
import { useCastCrewStore } from '@/stores/modules/castcrew';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const emit = defineEmits(['snack']);
const route = useRoute();
const releaseStore = useReleaseStore();
const formStore = useFormTemplateStore();
const castCrewStore = useCastCrewStore();
const overlay = ref<boolean>(true);
const showReleaseForm = ref<boolean>(true);
const errors = ref<boolean>(true);

const emailAddress = computed((): string =>
  Array.isArray(route.query.email_address)
    ? (route.query.email_address[0] as string)
    : route.query.email_address || '',
);
const token = computed((): string =>
  Array.isArray(route.query.token) ? (route.query.token[0] as string) : (route.query.token ?? ''),
);
const submissionId = computed((): string =>
  Array.isArray(route.query.submissionId)
    ? (route.query.submissionId[0] as string)
    : (route.query.submissionId ?? ''),
);

watch(
  [emailAddress, token, submissionId],
  () => {
    if (emailAddress.value && token.value && submissionId.value) {
      errors.value = false;
    } else {
      errors.value = true;
    }
  },
  { immediate: true },
);

const {
  data: releaseData,
  isLoading: isReleaseLoading,
  isError: releaseError,
} = releaseStore.getTalentRelease(emailAddress.value, token.value, errors);
const { data: talenForm } = formStore.getFormTemplateById(DEFAULT_TALENT_FORM);
const { data: releaseTypesData } = releaseStore.getReleaseTypes();

const fields = ref<IBaseFormFields>({
  date: currentDate.value,
  ipAddress: '',
  browser: browser.value,
  teamName: '',
});

const castCrew = ref<ISubmissionCastCrewDTO>(new SubmissionCastCrewDTO());
const release = ref<IReleaseDTO>(new ReleaseDTO());

const talentType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Talent.id) ||
    new ReleaseTypeDTO()
  );
});

const handleFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadSubmissionRelease(
    submissionId.value,
    talentType.value.id,
    file,
    updateProgress,
  );
  release.value.release = response;
  return response;
};

const handleUploadComplted = async (): Promise<void> => {
  release.value.releaseType = talentType.value;
  release.value.submission = new SubmissionDTO({ id: submissionId.value });
  castCrew.value.release = new ReleaseDTO(release.value);
  await castCrewStore.updateTalentRelease.mutateAsync(castCrew.value);
};

const handleSigUpload = async (file: File) => {
  const uploadProgress = ref<number>(0);
  const uploadCompleted = ref<boolean>(false);
  const progress = createProgressHandler(uploadProgress, uploadCompleted);
  await handleFileUpload(file, progress);
  await handleUploadComplted();
};

watch(
  () => releaseError.value,
  () => {
    console.error('Error fetching release data:', releaseError.value);
    errors.value = true;
  },
);

watch(
  () => releaseData.value,
  () => {
    if (releaseData.value) {
      castCrew.value = new SubmissionCastCrewDTO(releaseData.value);
      release.value = new ReleaseDTO(releaseData.value.release);
      fields.value.teamName = `${castCrew.value.first_name} ${castCrew.value.last_name}`;
      fields.value.name = `${castCrew.value.first_name} ${castCrew.value.last_name}`;
      fields.value.email = castCrew.value.email_address;
    }
    overlay.value = false;
  },
  { immediate: true },
);

watch(
  () => isReleaseLoading.value,
  (loading: boolean) => {
    if (!loading) {
      overlay.value = false;
    }
  },
);

watch(
  () => releaseData.value,
  (newValue) => {
    if (newValue && newValue.release && newValue.release.release) {
      showReleaseForm.value = false;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  fields.value.ipAddress = (await userIP.value) || '';
});
</script>
